import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {BellIcon, CheckIcon} from "@pg-design/icons-module";
import {BrandArrowIcon, LockOutlineIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";

import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {AuthModalVariant} from "../../auth/components/auth_modal/AuthModalHeaderVariants";
import {startRegistrationFlow} from "../../auth/state/registration_flow_slice";
import {useFavourites} from "../../favorite/hooks/use_favourites";
import {useGetPriceAlertInfo} from "../../favorite/hooks/use_get_price_alert_info";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useAppDispatch} from "../../store/hooks";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {useGetPropertyPriceHistoryQuery} from "../api/get_property_price_history_api";
import {useGetPropertyPriceHistoryValues} from "../hooks/use_get_property_price_history_values";
import {hitGTMPriceHistoryAlertClick, hitGTMPriceHistorySignUpClick} from "../tracking/hit_gtm_event_price_history";
import {IPropertyDetail} from "../types/IPropertyDetail";

interface IProps {
    property: IPropertyDetail;
}

export const PropertyDetailPriceHistory = (props: IProps) => {
    const {property} = props;

    const {data: priceHistory} = useGetPropertyPriceHistoryQuery(property.id);
    const {isMobile} = useUserDevice();
    const {addPropertyToFavourites, removePropertyFromFavourites} = useFavourites();
    const {hasAlertOn} = useGetPriceAlertInfo(property.id);

    const dispatch = useAppDispatch();
    const isAuthenticated = useIsAuthenticated();
    const isAbroad = property.offer.region.country !== Country.POLAND;

    const {hasCompleteProfile, hasPhoneVerified, isUserLoggedIn} = useShouldHidePrices();

    const {publicationDate, rows} = useGetPropertyPriceHistoryValues({
        priceHistory,
        isAuthenticated,
        isAbroad
    });

    const hasPrice = typeof property.price === "number" && property.price > 0;
    const hasPriceHistory = priceHistory && priceHistory.length > 0;

    if (!hasPriceHistory || !hasPrice) {
        return null;
    }

    const handlers = {
        //when proper logic for adding/removing price alerts is ready, replace the following two functions with it
        turnOnPriceAlert: async () => {
            hitGTMPriceHistoryAlertClick("Włącz alert cenowy");
            await addPropertyToFavourites(property.id, property.offer.id, true);
        },
        turnOffPriceAlert: async () => {
            hitGTMPriceHistoryAlertClick("Wyłącz alert cenowy");
            await removePropertyFromFavourites(property.id);
        }
    };

    const handleStartRegistrationFlow = (showLoginForm: boolean) => {
        dispatch(
            startRegistrationFlow({
                isUserLoggedIn,
                hasCompleteProfile,
                hasPhoneVerified,
                userSource: UserSource.PRICE_HISTORY,
                gtmSourceSection: GTMEventApplicationSourceSection.PRICE_HISTORY,
                modalVariant: "hidden_prices_variant_b",
                propertyId: property.id ?? undefined,
                offerId: property.offer.id ?? undefined,
                titleVariant: AuthModalVariant.PRICE_HISTORY,
                showLoginForm,
                amplitudePlacement: "rp_price_history"
            })
        );
    };

    const signUpClick = () => {
        hitGTMPriceHistorySignUpClick("Zarejestruj się");
        handleStartRegistrationFlow(false);
    };

    const signInClick = () => {
        hitGTMPriceHistorySignUpClick("Zaloguj się");
        handleStartRegistrationFlow(true);
    };

    const shouldShowPaywall = !isAuthenticated || !hasPhoneVerified;

    return (
        <div className={cx(sectionWrapperStyle, shouldShowPaywall && blurredPaddingStyle)} data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.PRICE_HISTORY_SECTION}>
            <div className={cx(cardStyle, shouldShowPaywall && cardBlurredStyle)} data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.PRICE_HISTORY_SECTION_CONTENT}>
                <header className={headerStyle}>
                    <BrandArrowIcon className={iconStyle} size="6" wrapperColor="var(--colors-primary)" wrapperSize="6" wrapperType="circle" />

                    <div>
                        <Text variant="headline_4">Historia cen</Text>
                        <div className={metaWrapperStyle}>
                            <Text variant="info_txt_1">
                                Publikacja: <strong>{publicationDate || "—"}</strong>
                            </Text>
                        </div>
                    </div>
                </header>

                <table className={tableStyle}>
                    <thead className={tableHeaderStyle}>
                        <tr>
                            <th className={tableColDateStyle}>
                                <Text variant="body_copy_2">Data</Text>
                            </th>

                            <th>
                                <Text variant="body_copy_2">Zmiana</Text>
                            </th>

                            <th className={tableColPriceStyle}>
                                <Text variant="body_copy_2">Cena</Text>
                            </th>
                        </tr>
                    </thead>

                    <tbody className={tableBodyStyle}>
                        {rows.length > 0
                            ? rows.map((rowData) => {
                                  const rowDataStyle = rowData.isIncrease ? badgeIncreaseStyle : rowData.isDecrease ? badgeDecreaseStyle : badgeNeutralStyle;

                                  return (
                                      <tr key={`${rowData.row.create_date}-${rowData.index}`} className={tableRowStyle}>
                                          <td className={tableColDateStyle}>
                                              <Text variant="body_copy_2">{rowData.formattedDate}</Text>
                                          </td>

                                          <td>
                                              <div className={changeCellContentStyle}>
                                                  <Text variant="body_copy_2">{rowData.changeAmountDisplay}</Text>

                                                  {rowData.changePercent !== 0 && (
                                                      <span className={rowDataStyle}>
                                                          {rowData.isIncrease ? "▲" : "▼"}{" "}
                                                          {!isAuthenticated ? "00" : Math.abs(rowData.changePercent).toFixed(0)}%
                                                      </span>
                                                  )}
                                              </div>
                                          </td>

                                          <td className={tableColPriceStyle}>
                                              <div className={priceCellContentStyle}>
                                                  <Text variant="body_copy_2" strong>
                                                      {rowData.priceTotalDisplay}
                                                  </Text>

                                                  <Text variant="info_txt_3" color="var(--colors-gray-700)">
                                                      {rowData.priceM2Display}
                                                  </Text>
                                              </div>
                                          </td>
                                      </tr>
                                  );
                              })
                            : null}
                    </tbody>
                </table>

                <div className={alertBannerStyle}>
                    <div className={bannerTextStyle}>
                        {hasAlertOn ? <CheckIcon size={isMobile ? "4" : "3"} /> : <BellIcon size={isMobile ? "4" : "3"} />}

                        <Text variant="body_copy_2">
                            {hasAlertOn ? "Masz włączone powiadomienia o zmianach cen tej oferty." : "Otrzymuj powiadomienia o zmianach cen tej oferty."}
                        </Text>
                    </div>

                    <Button
                        variant="outlined_secondary"
                        size="x-small"
                        className={alertButtonStyle}
                        onClick={hasAlertOn ? handlers.turnOffPriceAlert : handlers.turnOnPriceAlert}
                        dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.PRICE_HISTORY_ALERTS_BUTTON}
                    >
                        {hasAlertOn ? "Wyłącz alert cenowy" : "Włącz alert cenowy"}
                    </Button>
                </div>
            </div>

            {shouldShowPaywall && (
                <>
                    <div className={paywallBackdropStyle} data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.PRICE_HISTORY_PAYWALL_BACKDROP} />

                    <div className={paywallStyle}>
                        <LockOutlineIcon size="4" fill="var(--colors-white)" />

                        <Text variant="headline_5" align="center" color="var(--colors-white)">
                            {isAuthenticated ? "Zweryfikuj numer telefonu" : "Załóż konto"}, aby otrzymać dostęp
                            <br />
                            do <span className={css({fontWeight: "700"})}>pełnej historii</span> cen tej oferty
                        </Text>
                        {!isAuthenticated ? (
                            <>
                                <Button
                                    variant="filled_secondary"
                                    size="small"
                                    onClick={signUpClick}
                                    dataTestId={OFFER_TEST_IDS.OFFER_VIEW.REGISTER_FROM_HISTORY_PRICES}
                                >
                                    Zarejestruj się
                                </Button>

                                <Button
                                    variant="none_secondary"
                                    size="small"
                                    className={whiteButtonTextStyle}
                                    onClick={signInClick}
                                    dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.PRICE_HISTORY_LOGIN_BUTTON}
                                >
                                    Zaloguj się
                                </Button>
                            </>
                        ) : (
                            <Button variant="filled_secondary" size="small" onClick={() => handleStartRegistrationFlow(false)}>
                                Zweryfikuj numer
                            </Button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

const sectionWrapperStyle = css({
    position: "relative",
    backgroundColor: "var(--colors-white)",
    borderRadius: 2,
    p: 1,
    mb: 2,

    sm: {
        p: 2
    }
});

const cardStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 3
});

const cardBlurredStyle = css({
    filter: "blur(6px)",
    userSelect: "none",
    pointerEvents: "none"
});

const blurredPaddingStyle = css({
    py: 9,
    sm: {
        py: 7
    }
});

const headerStyle = css({
    display: "flex",
    flexDirection: "row",
    gap: 2,
    p: 1,

    sm: {
        p: 0
    }
});

const iconStyle = css({
    alignSelf: "center"
});

const metaWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 0,
    mt: 1
});

const tableStyle = css({
    mt: 2,
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0
});

const tableBodyStyle = css({
    display: "block",
    maxHeight: "220px", // 5 rows * ~44px
    overflowY: "auto",
    backgroundColor: "var(--colors-gray-100)",
    borderRadius: 1,
    "&::-webkit-scrollbar": {
        width: "6px"
    },
    "&::-webkit-scrollbar-track": {
        background: "transparent"
    },
    "&::-webkit-scrollbar-thumb": {
        background: "var(--colors-gray-300)",
        borderRadius: "3px"
    },
    "& tr": {
        display: "table",
        width: "100%"
    },
    "& tr:first-of-type td:first-of-type": {
        borderTopLeftRadius: 1
    },
    "& tr:first-of-type td:last-of-type": {
        borderTopRightRadius: 1
    },
    "& tr:last-of-type td:first-of-type": {
        borderBottomLeftRadius: 1
    },
    "& tr:last-of-type td:last-of-type": {
        borderBottomRightRadius: 1
    }
});

const tableHeaderStyle = css({
    display: "table",
    width: "100%",
    "& th": {
        textAlign: "left",
        p: 1
    }
});

const tableRowStyle = css({
    "& td": {
        verticalAlign: "middle",
        p: 1,
        borderTop: "1px solid var(--colors-gray-200)"
    },
    "&:first-of-type td": {
        borderTop: "none"
    }
});

const tableColDateStyle = css({
    width: "30%",

    sm: {
        width: "40%"
    }
});

const changeCellContentStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 1.5,
    maxWidth: "130px",

    sm: {
        maxWidth: "165px"
    }
});

const tableColPriceStyle = css({
    width: "90px",
    whiteSpace: "nowrap",

    sm: {
        width: "100px"
    }
});

const priceCellContentStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end"
});

const badgeBaseStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "right",
    borderRadius: 0.5,
    backgroundColor: "var(--colors-white)",
    px: 0.5,
    fontSize: "1rem",
    marginLeft: "auto",

    sm: {
        fontSize: "1.4rem",
        px: 1
    }
};

const badgeIncreaseStyle = css({
    ...badgeBaseStyle,
    color: "var(--colors-danger)"
});

const badgeDecreaseStyle = css({
    ...badgeBaseStyle,
    color: "var(--globals-checkbox-checkedColor)"
});

const badgeNeutralStyle = css({
    ...badgeBaseStyle
});

const paywallBackdropStyle = css({
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "var(--colors-secondary)",
    opacity: 0.75,
    borderRadius: 2,
    zIndex: 5
});

const paywallStyle = css({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 3,
    zIndex: 5
});

const whiteButtonTextStyle = css({
    color: "var(--colors-white)"
});

const alertBannerStyle = css({
    backgroundColor: "var(--colors-success)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    p: 1.5,
    pt: 2,
    borderRadius: 2,
    gap: 3,

    sm: {
        flexDirection: "row",
        justifyContent: "space-between",
        gap: 0,
        p: 1.5
    }
});

const bannerTextStyle = css({
    display: "flex",
    flexDirection: "row",
    gap: 2.5,
    alignItems: "center",
    pl: 1,

    sm: {
        pl: 1.5
    }
});

const alertButtonStyle = css({
    width: "100%",

    sm: {
        width: "auto"
    }
});
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {BellIcon, CheckIcon} from "@pg-design/icons-module";
import {BrandArrowIcon, LockOutlineIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";

import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {AuthModalVariant} from "../../auth/components/auth_modal/AuthModalHeaderVariants";
import {startRegistrationFlow} from "../../auth/state/registration_flow_slice";
import {useFavourites} from "../../favorite/hooks/use_favourites";
import {useGetPriceAlertInfo} from "../../favorite/hooks/use_get_price_alert_info";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useAppDispatch} from "../../store/hooks";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {useGetPropertyPriceHistoryQuery} from "../api/get_property_price_history_api";
import {useGetPropertyPriceHistoryValues} from "../hooks/use_get_property_price_history_values";
import {hitGTMPriceHistoryAlertClick, hitGTMPriceHistorySignUpClick} from "../tracking/hit_gtm_event_price_history";
import {IPropertyDetail} from "../types/IPropertyDetail";

interface IProps {
    property: IPropertyDetail;
}

export const PropertyDetailPriceHistory = (props: IProps) => {
    const {property} = props;

    const {data: priceHistory} = useGetPropertyPriceHistoryQuery(property.id);
    const {isMobile} = useUserDevice();
    const {addPropertyToFavourites, removePropertyFromFavourites} = useFavourites();
    const {hasAlertOn} = useGetPriceAlertInfo(property.id);

    const dispatch = useAppDispatch();
    const isAuthenticated = useIsAuthenticated();
    const isAbroad = property.offer.region.country !== Country.POLAND;

    const {hasCompleteProfile, hasPhoneVerified, isUserLoggedIn} = useShouldHidePrices();

    const {publicationDate, rows} = useGetPropertyPriceHistoryValues({
        priceHistory,
        isAuthenticated,
        isAbroad
    });

    const hasPrice = typeof property.price === "number" && property.price > 0;
    const hasPriceHistory = priceHistory && priceHistory.length > 0;

    if (!hasPriceHistory || !hasPrice) {
        return null;
    }

    const handlers = {
        //when proper logic for adding/removing price alerts is ready, replace the following two functions with it
        turnOnPriceAlert: async () => {
            hitGTMPriceHistoryAlertClick("Włącz alert cenowy");
            await addPropertyToFavourites(property.id, property.offer.id, true);
        },
        turnOffPriceAlert: async () => {
            hitGTMPriceHistoryAlertClick("Wyłącz alert cenowy");
            await removePropertyFromFavourites(property.id);
        }
    };

    const handleStartRegistrationFlow = (showLoginForm: boolean) => {
        dispatch(
            startRegistrationFlow({
                isUserLoggedIn,
                hasCompleteProfile,
                hasPhoneVerified,
                userSource: UserSource.PRICE_HISTORY,
                gtmSourceSection: GTMEventApplicationSourceSection.PRICE_HISTORY,
                modalVariant: "hidden_prices_variant_b",
                propertyId: property.id ?? undefined,
                offerId: property.offer.id ?? undefined,
                titleVariant: AuthModalVariant.PRICE_HISTORY,
                showLoginForm,
                amplitudePlacement: "rp_price_history"
            })
        );
    };

    const signUpClick = () => {
        hitGTMPriceHistorySignUpClick("Zarejestruj się");
        handleStartRegistrationFlow(false);
    };

    const signInClick = () => {
        hitGTMPriceHistorySignUpClick("Zaloguj się");
        handleStartRegistrationFlow(true);
    };

    const shouldShowPaywall = !isAuthenticated || !hasPhoneVerified;

    return (
        <div className={cx(sectionWrapperStyle, shouldShowPaywall && blurredPaddingStyle)} data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.PRICE_HISTORY_SECTION}>
            <div className={cx(cardStyle, shouldShowPaywall && cardBlurredStyle)} data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.PRICE_HISTORY_SECTION_CONTENT}>
                <header className={headerStyle}>
                    <BrandArrowIcon className={iconStyle} size="6" wrapperColor="var(--colors-primary)" wrapperSize="6" wrapperType="circle" />

                    <div>
                        <Text variant="headline_4">Historia cen</Text>
                        <div className={metaWrapperStyle}>
                            <Text variant="info_txt_1">
                                Publikacja: <strong>{publicationDate || "—"}</strong>
                            </Text>
                        </div>
                    </div>
                </header>

                <table className={tableStyle}>
                    <thead className={tableHeaderStyle}>
                        <tr>
                            <th className={tableColDateStyle}>
                                <Text variant="body_copy_2">Data</Text>
                            </th>

                            <th>
                                <Text variant="body_copy_2">Zmiana</Text>
                            </th>

                            <th className={tableColPriceStyle}>
                                <Text variant="body_copy_2">Cena</Text>
                            </th>
                        </tr>
                    </thead>

                    <tbody className={tableBodyStyle}>
                        {rows.length > 0
                            ? rows.map((rowData) => {
                                  const rowDataStyle = rowData.isIncrease ? badgeIncreaseStyle : rowData.isDecrease ? badgeDecreaseStyle : badgeNeutralStyle;

                                  return (
                                      <tr key={`${rowData.row.create_date}-${rowData.index}`} className={tableRowStyle}>
                                          <td className={tableColDateStyle}>
                                              <Text variant="body_copy_2">{rowData.formattedDate}</Text>
                                          </td>

                                          <td>
                                              <div className={changeCellContentStyle}>
                                                  <Text variant="body_copy_2">{rowData.changeAmountDisplay}</Text>

                                                  {rowData.changePercent !== 0 && (
                                                      <span className={rowDataStyle}>
                                                          {rowData.isIncrease ? "▲" : "▼"}{" "}
                                                          {!isAuthenticated ? "00" : Math.abs(rowData.changePercent).toFixed(0)}%
                                                      </span>
                                                  )}
                                              </div>
                                          </td>

                                          <td className={tableColPriceStyle}>
                                              <div className={priceCellContentStyle}>
                                                  <Text variant="body_copy_2" strong>
                                                      {rowData.priceTotalDisplay}
                                                  </Text>

                                                  <Text variant="info_txt_3" color="var(--colors-gray-700)">
                                                      {rowData.priceM2Display}
                                                  </Text>
                                              </div>
                                          </td>
                                      </tr>
                                  );
                              })
                            : null}
                    </tbody>
                </table>

                <div className={alertBannerStyle}>
                    <div className={bannerTextStyle}>
                        {hasAlertOn ? <CheckIcon size={isMobile ? "4" : "3"} /> : <BellIcon size={isMobile ? "4" : "3"} />}

                        <Text variant="body_copy_2">
                            {hasAlertOn ? "Masz włączone powiadomienia o zmianach cen tej oferty." : "Otrzymuj powiadomienia o zmianach cen tej oferty."}
                        </Text>
                    </div>

                    <Button
                        variant="outlined_secondary"
                        size="x-small"
                        className={alertButtonStyle}
                        onClick={hasAlertOn ? handlers.turnOffPriceAlert : handlers.turnOnPriceAlert}
                        dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.PRICE_HISTORY_ALERTS_BUTTON}
                    >
                        {hasAlertOn ? "Wyłącz alert cenowy" : "Włącz alert cenowy"}
                    </Button>
                </div>
            </div>

            {shouldShowPaywall && (
                <>
                    <div className={paywallBackdropStyle} data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.PRICE_HISTORY_PAYWALL_BACKDROP} />

                    <div className={paywallStyle}>
                        <LockOutlineIcon size="4" fill="var(--colors-white)" />

                        <Text variant="headline_5" align="center" color="var(--colors-white)">
                            {isAuthenticated ? "Zweryfikuj numer telefonu" : "Załóż konto"}, aby otrzymać dostęp
                            <br />
                            do <span className={css({fontWeight: "700"})}>pełnej historii</span> cen tej oferty
                        </Text>
                        {!isAuthenticated ? (
                            <>
                                <Button
                                    variant="filled_secondary"
                                    size="small"
                                    onClick={signUpClick}
                                    dataTestId={OFFER_TEST_IDS.OFFER_VIEW.REGISTER_FROM_HISTORY_PRICES}
                                >
                                    Zarejestruj się
                                </Button>

                                <Button
                                    variant="none_secondary"
                                    size="small"
                                    className={whiteButtonTextStyle}
                                    onClick={signInClick}
                                    dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.PRICE_HISTORY_LOGIN_BUTTON}
                                >
                                    Zaloguj się
                                </Button>
                            </>
                        ) : (
                            <Button variant="filled_secondary" size="small" onClick={() => handleStartRegistrationFlow(false)}>
                                Zweryfikuj numer
                            </Button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

const sectionWrapperStyle = css({
    position: "relative",
    backgroundColor: "var(--colors-white)",
    borderRadius: 2,
    p: 1,
    mb: 2,

    sm: {
        p: 2
    }
});

const cardStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 3
});

const cardBlurredStyle = css({
    filter: "blur(6px)",
    userSelect: "none",
    pointerEvents: "none"
});

const blurredPaddingStyle = css({
    py: 9,
    sm: {
        py: 7
    }
});

const headerStyle = css({
    display: "flex",
    flexDirection: "row",
    gap: 2,
    p: 1,

    sm: {
        p: 0
    }
});

const iconStyle = css({
    alignSelf: "center"
});

const metaWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 0,
    mt: 1
});

const tableStyle = css({
    mt: 2,
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0
});

const tableBodyStyle = css({
    display: "block",
    maxHeight: "220px", // 5 rows * ~44px
    overflowY: "auto",
    backgroundColor: "var(--colors-gray-100)",
    borderRadius: 1,
    "&::-webkit-scrollbar": {
        width: "6px"
    },
    "&::-webkit-scrollbar-track": {
        background: "transparent"
    },
    "&::-webkit-scrollbar-thumb": {
        background: "var(--colors-gray-300)",
        borderRadius: "3px"
    },
    "& tr": {
        display: "table",
        width: "100%"
    },
    "& tr:first-of-type td:first-of-type": {
        borderTopLeftRadius: 1
    },
    "& tr:first-of-type td:last-of-type": {
        borderTopRightRadius: 1
    },
    "& tr:last-of-type td:first-of-type": {
        borderBottomLeftRadius: 1
    },
    "& tr:last-of-type td:last-of-type": {
        borderBottomRightRadius: 1
    }
});

const tableHeaderStyle = css({
    display: "table",
    width: "100%",
    "& th": {
        textAlign: "left",
        p: 1
    }
});

const tableRowStyle = css({
    "& td": {
        verticalAlign: "middle",
        p: 1,
        borderTop: "1px solid var(--colors-gray-200)"
    },
    "&:first-of-type td": {
        borderTop: "none"
    }
});

const tableColDateStyle = css({
    width: "30%",

    sm: {
        width: "40%"
    }
});

const changeCellContentStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 1.5,
    maxWidth: "130px",

    sm: {
        maxWidth: "165px"
    }
});

const tableColPriceStyle = css({
    width: "90px",
    whiteSpace: "nowrap",

    sm: {
        width: "100px"
    }
});

const priceCellContentStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end"
});

const badgeBaseStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "right",
    borderRadius: 0.5,
    backgroundColor: "var(--colors-white)",
    px: 0.5,
    fontSize: "1rem",
    marginLeft: "auto",

    sm: {
        fontSize: "1.4rem",
        px: 1
    }
};

const badgeIncreaseStyle = css({
    ...badgeBaseStyle,
    color: "var(--colors-danger)"
});

const badgeDecreaseStyle = css({
    ...badgeBaseStyle,
    color: "var(--globals-checkbox-checkedColor)"
});

const badgeNeutralStyle = css({
    ...badgeBaseStyle
});

const paywallBackdropStyle = css({
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "var(--colors-secondary)",
    opacity: 0.75,
    borderRadius: 2,
    zIndex: 5
});

const paywallStyle = css({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 3,
    zIndex: 5
});

const whiteButtonTextStyle = css({
    color: "var(--colors-white)"
});

const alertBannerStyle = css({
    backgroundColor: "var(--colors-success)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    p: 1.5,
    pt: 2,
    borderRadius: 2,
    gap: 3,

    sm: {
        flexDirection: "row",
        justifyContent: "space-between",
        gap: 0,
        p: 1.5
    }
});

const bannerTextStyle = css({
    display: "flex",
    flexDirection: "row",
    gap: 2.5,
    alignItems: "center",
    pl: 1,

    sm: {
        pl: 1.5
    }
});

const alertButtonStyle = css({
    width: "100%",

    sm: {
        width: "auto"
    }
});
